import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import type { FaultType, ShortCircuitCase } from "../services/faultContract";
import {
  scpdKindSchema,
  starterCoordinationTypeSchema,
  startingMethodSchema,
  type ScpdKind,
  type StarterCoordinationType,
  type StartingMethod,
} from "../services/starterCoordinationContract";
import type { ValidationLabels } from "../utils/validationMessages";

// Draft model of the Type 1 / Type 2 starter coordination form (EOS-05a, A20):
// what the user is typing, kept apart from the components so the payload rules
// are tested without a browser.
//
// Every value is held as the string the user typed. The fault level comes from
// one of two places (A20 (d)): a stored EOS-04 fault run of the active revision
// (LINKED, the default) or a typed value (TYPED, which the engine reports as
// REVIEW_REQUIRED). The payload carries only the chosen source. A blank
// certificate item (Iq, Ue, certificate reference) is left OUT of the payload:
// under A20 (e) blank means NOT ESTABLISHED, and the engine must see it as
// absent, never as "". Every other row input is required, so a blank one is
// sent as "" for the schema to report by its on-screen label; a choice the user
// has not made ("" = Choose...) is left out so the schema reports it as required.
//
// Rows are separate entries with stable ids, so adding or removing one never
// disturbs the others; the ids never reach the backend.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const STARTER_COORDINATION_DRAFT_MODULE = "starter-coordination";

/** Where the fault level comes from, on screen: the linked run or a typed value. */
export const FAULT_LEVEL_SOURCES = ["LINKED", "TYPED"] as const;

export type FaultLevelSource = (typeof FAULT_LEVEL_SOURCES)[number];

export type StarterRowDraft = {
  id: string;
  motorCode: string;
  ratedPowerKw: string;
  fullLoadCurrentA: string;
  startingMethod: string;
  requiredCoordinationType: string;
  scpdKind: string;
  scpdRatingA: string;
  contactorAc3RatingA: string;
  overloadSettingMinA: string;
  overloadSettingMaxA: string;
  declaredCoordinationType: string;
  certifiedIqKa: string;
  testVoltageV: string;
  certificateReference: string;
};

export type StarterCoordinationDraft = {
  code: string;
  name: string;
  busCode: string;
  faultLevelSource: FaultLevelSource;
  faultRunId: string;
  typedProspectiveCurrentKa: string;
  typedSystemNominalVoltageV: string;
  typedFaultBusCode: string;
  rows: StarterRowDraft[];
  jurisdictionProfile: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const starterRowDraftSchema = z
  .object({
    id: z.string(),
    motorCode: z.string(),
    ratedPowerKw: z.string(),
    fullLoadCurrentA: z.string(),
    startingMethod: chosenOrBlank(startingMethodSchema),
    requiredCoordinationType: chosenOrBlank(starterCoordinationTypeSchema),
    scpdKind: chosenOrBlank(scpdKindSchema),
    scpdRatingA: z.string(),
    contactorAc3RatingA: z.string(),
    overloadSettingMinA: z.string(),
    overloadSettingMaxA: z.string(),
    declaredCoordinationType: chosenOrBlank(starterCoordinationTypeSchema),
    certifiedIqKa: z.string(),
    testVoltageV: z.string(),
    certificateReference: z.string(),
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values the contract knows or "".
// A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const starterCoordinationDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    faultLevelSource: z.enum(FAULT_LEVEL_SOURCES),
    faultRunId: z.string(),
    typedProspectiveCurrentKa: z.string(),
    typedSystemNominalVoltageV: z.string(),
    typedFaultBusCode: z.string(),
    rows: z.array(starterRowDraftSchema).min(1),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

let nextRowNumber = 1;

// A prefix of its own, so a starter row id is never mistaken for another form's.
export function createStarterRowId(): string {
  const id = `starter-${nextRowNumber}`;
  nextRowNumber += 1;
  return id;
}

// Row ids come from a counter that starts again at 1 on every page load, while a
// restored draft brings the ids it was saved with. Moving the counter past every id in use
// keeps a row added afterwards from taking the id of one already on screen.
export function seedStarterRowIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^starter-(\d+)$/.exec(id);
    if (match !== null) {
      nextRowNumber = Math.max(nextRowNumber, Number(match[1]) + 1);
    }
  }
}

export function createStarterRowDraft(): StarterRowDraft {
  return {
    id: createStarterRowId(),
    motorCode: "",
    ratedPowerKw: "",
    fullLoadCurrentA: "",
    startingMethod: "",
    requiredCoordinationType: "",
    scpdKind: "",
    scpdRatingA: "",
    contactorAc3RatingA: "",
    overloadSettingMinA: "",
    overloadSettingMaxA: "",
    declaredCoordinationType: "",
    certifiedIqKa: "",
    testVoltageV: "",
    certificateReference: "",
  };
}

// Only the defaults the backend itself has are filled in: the IN profile. The fault
// level starts on the linked run, as A20 (d) wants it, with no run chosen yet; one
// empty starter row waits for the engineer; every choice starts blank.
export function createInitialStarterCoordinationDraft(): StarterCoordinationDraft {
  return {
    code: "",
    name: "",
    busCode: "",
    faultLevelSource: "LINKED",
    faultRunId: "",
    typedProspectiveCurrentKa: "",
    typedSystemNominalVoltageV: "",
    typedFaultBusCode: "",
    rows: [createStarterRowDraft()],
    jurisdictionProfile: "IN",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// One starter as the request carries it. Required text and figures go as typed
// (trimmed, blank as "") so the schema names the blank by its label; an unmade
// choice is left out so the schema reports it as required; a blank certificate
// item is left out because it means NOT ESTABLISHED (A20 (e)).
function rowPayload(row: StarterRowDraft): Record<string, unknown> {
  return {
    motor_code: row.motorCode.trim(),
    rated_power_kw: row.ratedPowerKw.trim(),
    full_load_current_a: row.fullLoadCurrentA.trim(),
    ...optional("starting_method", row.startingMethod),
    ...optional("required_coordination_type", row.requiredCoordinationType),
    ...optional("scpd_kind", row.scpdKind),
    scpd_rating_a: row.scpdRatingA.trim(),
    contactor_ac3_rating_a: row.contactorAc3RatingA.trim(),
    overload_setting_min_a: row.overloadSettingMinA.trim(),
    overload_setting_max_a: row.overloadSettingMaxA.trim(),
    ...optional("declared_coordination_type", row.declaredCoordinationType),
    ...optional("certified_iq_ka", row.certifiedIqKa),
    ...optional("test_voltage_v", row.testVoltageV),
    ...optional("certificate_reference", row.certificateReference),
  };
}

// The fault level as chosen on screen: the linked run's id alone, or the typed
// block alone (A20 (d)). A blank run id or a blank typed figure is sent as "" so
// the schema names it; the builder never sends both sources.
function faultLevelPayload(draft: StarterCoordinationDraft): Record<string, unknown> {
  if (draft.faultLevelSource === "TYPED") {
    return {
      typed: {
        prospective_current_ka: draft.typedProspectiveCurrentKa.trim(),
        system_nominal_voltage_v: draft.typedSystemNominalVoltageV.trim(),
        fault_bus_code: draft.typedFaultBusCode.trim(),
      },
    };
  }
  return { fault_run_id: draft.faultRunId.trim() };
}

// The request body as the form will send it. It is validated afterwards with
// starterCoordinationRunCreateRequestSchema, so this function does not judge the
// values and never reorders the rows.
export function buildStarterCoordinationRunPayload(
  draft: StarterCoordinationDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      bus_code: draft.busCode.trim(),
      ...faultLevelPayload(draft),
      // Every row is sent, so the number in a message such as "Starter 2 — Motor code
      // is required." always matches the row's position on screen.
      rows: draft.rows.map(rowPayload),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// On-screen labels of the starter coordination form, keyed by the request path.
// The run request nests the study under "study", so every study path carries
// that prefix; describeLocation numbers the rows, giving "Starter 2 — ...".
export const STARTER_COORDINATION_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.bus_code": "Bus code",
    "study.fault_run_id": "Fault run",
    "study.typed": "Fault level source",
    "study.typed.prospective_current_ka": "Prospective current Ik'' (kA)",
    "study.typed.system_nominal_voltage_v": "System nominal voltage (V)",
    "study.typed.fault_bus_code": "Fault bus code",
    "study.rows": "Starters",
    "study.rows.*.motor_code": "Motor code",
    "study.rows.*.rated_power_kw": "Rated output (kW)",
    "study.rows.*.full_load_current_a": "Full-load current FLC (A)",
    "study.rows.*.starting_method": "Starting method",
    "study.rows.*.required_coordination_type": "Required coordination",
    "study.rows.*.scpd_kind": "SCPD type",
    "study.rows.*.scpd_rating_a": "SCPD rating (A)",
    "study.rows.*.contactor_ac3_rating_a": "Contactor AC-3 rating (A)",
    "study.rows.*.overload_setting_min_a": "Overload setting minimum (A)",
    "study.rows.*.overload_setting_max_a": "Overload setting maximum (A)",
    "study.rows.*.declared_coordination_type": "Declared coordination",
    "study.rows.*.certified_iq_ka": "Certified Iq (kA)",
    "study.rows.*.test_voltage_v": "Test voltage Ue (V)",
    "study.rows.*.certificate_reference": "Certificate reference",
    "study.jurisdiction_profile": "Jurisdiction profile",
    notes: "Study notes",
  },
  // One row of the starter table: "Starter 2".
  groups: { rows: "Starter" },
};

// The words of every choice the form offers, typed against the contract enums
// so a value the contract gains cannot pass without a label.

export const STARTING_METHOD_LABELS: Record<StartingMethod, string> = {
  DOL: "Direct on-line (DOL)",
  STAR_DELTA: "Star-delta",
  SOFT_STARTER: "Soft starter",
  VFD: "Variable frequency drive (VFD)",
};

export const COORDINATION_TYPE_LABELS: Record<StarterCoordinationType, string> = {
  TYPE_1: "Type 1",
  TYPE_2: "Type 2",
};

export const SCPD_KIND_LABELS: Record<ScpdKind, string> = {
  CIRCUIT_BREAKER: "Circuit breaker",
  FUSE: "Fuse",
};

export const FAULT_LEVEL_SOURCE_LABELS: Record<FaultLevelSource, string> = {
  LINKED: "Linked fault run",
  TYPED: "Typed value (review required)",
};

// The words of the linked fault run's case and fault type, typed against the fault
// contract so a value the contract gains cannot pass without words.
export const CASE_LABELS: Record<ShortCircuitCase, string> = {
  MAXIMUM: "Maximum",
  MINIMUM: "Minimum",
};

export const FAULT_TYPE_LABELS: Record<FaultType, string> = {
  THREE_PHASE: "Three-phase",
  TWO_PHASE: "Two-phase",
  TWO_PHASE_TO_EARTH: "Two-phase to earth",
  SINGLE_PHASE_TO_EARTH: "Single-phase to earth",
};
